import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronDown, ArrowRight } from 'lucide-react';

export default function Arkitektur() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative h-[600px] mb-0 overflow-hidden"
      >
        <img
          src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f2bf61e21_Nowoczesnasaunakompleksmedtresekcjoner.png"
          alt="Arkitektur"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 flex flex-col justify-between p-12 z-10">

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-lg tracking-widest-2xl font-body uppercase text-white/60 mb-6">Artikkel</p>
            <h1 className="font-heading text-6xl md:text-7xl lg:text-8xl font-light leading-[1.0] text-white mb-6">
              Arkitektur
            </h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Form, volum og forhold til stedet, arkitektur som møter landskapet
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12">

        {/* 1. GRUNNLAG FOR DESIGNET - FINNISH INSPIRATION */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
          <div className="max-w-4xl bg-secondary/30 rounded-[8px] p-12">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Grunnlag for designet</p>
            <h2 className="font-heading text-4xl font-light leading-[1.1] mb-8 text-[#2a1810]">
              Inspirasjon fra <span className="italic">Finland</span>
            </h2>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
              Ved starten av prosjektet LYKKE studerte vi grundig finsk badstuearkitektur og tradisjon. Vi leste klassiske værker om hvordan finnene — som er verdens eksperter på badstuer — har bygget og tenkt rundt disse bygningene i over hundre år.
            </p>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
              Fra disse bøkene lærte vi at en god sauna ikke handler om luksuriøse detaljer, men om:
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex gap-3">
                <span className="text-primary font-light text-xl">•</span>
                <span className="font-body text-2xl text-foreground/70">Proporsjonene mellom bredde, lengde og høyde</span>
              </li>
              <li className="flex gap-3">
                <span className="text-primary font-light text-xl">•</span>
                <span className="font-body text-2xl text-foreground/70">Hvordan luften skal strømme og varmen fordele seg</span>
              </li>
              <li className="flex gap-3">
                <span className="text-primary font-light text-xl">•</span>
                <span className="font-body text-2xl text-foreground/70">Materialenes rolle i å skape sikker og autentisk opplevelse</span>
              </li>
              <li className="flex gap-3">
                <span className="text-primary font-light text-xl">•</span>
                <span className="font-body text-2xl text-foreground/70">Minimalistisk filosofi — mindre er mer</span>
              </li>
            </ul>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed">
              Denne kunnskapen fra Finland, kombinert med norsk og skandinavisk arkitektur, dannet grunnlaget for vår tilnærming. Det er derfor alle detaljer i LYKKE saunene — fra volum og proportjoner til materialvalg — er bevisst gjennomtenkt.
            </p>
          </div>
        </motion.section>

        {/* IMAGE: Architect with plans */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-16">
          <div className="rounded-[8px] overflow-hidden">
            <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8cdee55d3_Zrzutekranu2026-04-11o141524.png" alt="Arkitekt studerer tegninger" className="w-full h-auto object-cover" />
          </div>
        </motion.div>

        {/* 2. OPPLEVELSEN - LØYLY */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-20">
          <div className="max-w-4xl">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Opplevelsen</p>
            <h2 className="font-heading text-4xl font-light leading-[1.1] mb-8 text-[#2a1810]">
              Løyly — <span className="italic">hjertet av badstueopplevelsen</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
                  Løyly er det finske ordet for den magiske opplevelsen av varme, damp og lys i en autentisk badstue. Det er ikke bare temperaturen — det er hvordan varmen strømmer, hvordan dunsten dannes, og hvordan arkitekturen rundt deg påvirker denne opplevelsen.
                </p>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
                  En godt utformet badstue skaper optimal løyly gjennom:
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex gap-3">
                    <span className="text-primary font-light text-xl">•</span>
                    <span className="font-body text-2xl text-foreground/70"><strong className="text-[#2a1810]">Riktig volum og høyde</strong> — Varmen skal strømme naturlig fra varmekilden oppover</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-light text-xl">•</span>
                    <span className="font-body text-2xl text-foreground/70"><strong className="text-[#2a1810]">Plassering av benker</strong> — Hvor du sitter bestemmer temperaturopplevelsen og kontakten med varmen</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-light text-xl">•</span>
                    <span className="font-body text-2xl text-foreground/70"><strong className="text-[#2a1810]">Lysåpninger</strong> — Naturlig lys gjennom små vinduer skaper intim atmosfære</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-light text-xl">•</span>
                    <span className="font-body text-2xl text-foreground/70"><strong className="text-[#2a1810]">Materialvalg</strong> — Tre skal tåle fuktighet og varme uten å gi fra seg giftige stoffer</span>
                  </li>
                </ul>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Løyly handler om at arkitekturen skal tjene opplevelsen — ikke omvendt. Det er grunnen til at vi er nøye med proporsjoner, materialvalg og detaljer.
                </p>
              </div>
              <div className="bg-gradient-to-br from-[#2d5a3d]/10 to-transparent border-l-4 border-[#2d5a3d] rounded-[8px] p-8">
                <p className="font-heading text-lg tracking-widest uppercase text-[#2d5a3d] mb-4">Løyly i praksis</p>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Når du kaster vann på steinen i en LYKKE badstue, skal dampen stige mykt og fylle rommet med varmestråling — ikke brent luft eller krevende kjemikalier. Det er resultatet av at alt — fra volumet til materialet på benken — er tenkt for å skape autentisk løyly.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* WELCOME PACK SECTION */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-20">
          <div className="max-w-4xl">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Essensielt tilbehør</p>
            <h2 className="font-heading text-4xl font-light leading-[1.1] mb-8 text-[#2a1810]">
              Welcome Pack. Holy Löyly. By LYKKE.
            </h2>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-8">
              I LYKKE tror vi at den virkelige badstueopplevelsen ikke begynner med temperaturen, men med ritualet. Desto når vi designet våre badstuer, antok vi at hver modell må være utstyrt med et Welcome Pack fra LYKKE Holy Löyly. Uten det oppstår ikke det som er viktigst — Löyly. <span className="font-semibold text-[#2a1810]">Det er ikke et tillegg. Det er fundamentet.</span> Og det kommer <span className="font-semibold text-[#2a1810]">helt gratis</span> med hver badstue.
            </p>
            <div className="grid md:grid-cols-2 gap-12 items-start mb-12">
              <div>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
                  I settpakken finner du alt du trenger for å skape ideelle vilkår for en fullverdig saunaøkt:
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2d5a3d] font-light text-lg">•</span>
                    </div>
                    <div>
                      <p className="font-heading text-sm tracking-widest uppercase text-[#2a1810] mb-1">Elegant bøtte</p>
                      <p className="font-body text-2xl text-foreground/60">Til å forberede vannet som skal helles på steinene</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2d5a3d] font-light text-lg">•</span>
                    </div>
                    <div>
                      <p className="font-heading text-sm tracking-widest uppercase text-[#2a1810] mb-1">Funksjonell øse</p>
                      <p className="font-body text-2xl text-foreground/60">Ergonomisk og lett, for presist dosering av vann</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2d5a3d] font-light text-lg">•</span>
                    </div>
                    <div>
                      <p className="font-heading text-sm tracking-widest uppercase text-[#2a1810] mb-1">Termohygrometer</p>
                      <p className="font-body text-2xl text-foreground/60">For å kontrollere temperaturen og fuktigheten i saunaen</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2d5a3d] font-light text-lg">•</span>
                    </div>
                    <div>
                      <p className="font-heading text-sm tracking-widest uppercase text-[#2a1810] mb-1">Timeglass</p>
                      <p className="font-body text-2xl text-foreground/60">Slik at tiden i saunaen flyter rolig og sesjonene blir balansert</p>
                    </div>
                  </li>
                </ul>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Alle elementene er laget av høykvalitets tre og holdbart materiale som tåler temperaturer og fuktighet — så de tjener deg i årevis. Hele designet harmoniserer naturlig med interiøret i en moderne LYKKE sauna.
                </p>
              </div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="space-y-4">
                <p className="font-heading text-sm tracking-widest uppercase text-[#2a1810] mb-4">Velg din favorittfarge</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-[8px] overflow-hidden">
                    <img src="https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/03dffceff_Harvia-Helmi-Sauna-Accessory-Set-Chocolate.jpg" alt="Welcome Pack Chocolate" className="w-full h-auto object-cover" />
                    <p className="font-body text-sm text-foreground/70 mt-3 font-semibold text-center">Chocolate</p>
                  </div>
                  <div className="rounded-[8px] overflow-hidden">
                    <img src="https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/66e6fffaf_Harvia-Helmi-Sauna-Accessory-Set-Terracotta.jpg" alt="Welcome Pack Terrakotta" className="w-full h-auto object-cover" />
                    <p className="font-body text-sm text-foreground/70 mt-3 font-semibold text-center">Terrakotta</p>
                  </div>
                </div>
                <p className="font-body text-2xl text-foreground/60 mt-4 italic">
                  Vi har forberedt to varianter av Welcome Pack Holy Löyly til valg — klassisk Chocolate eller varmt Terrakotta. Begge farger harmonerer perfekt med moderne og tradisjonelle saunainteriorer.
                </p>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="rounded-[8px] overflow-hidden mb-12">
              <img src="https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/b1fdee566_pexels-huum-sauna-heaters-718199222-31092914.jpg" alt="Ritual av vann på steiner" className="w-full h-auto object-cover" />
            </motion.div>
            <div className="bg-gradient-to-br from-[#2d5a3d]/10 to-transparent border-l-4 border-[#2d5a3d] rounded-[8px] p-8">
              <p className="font-heading text-lg tracking-widest uppercase text-[#2d5a3d] mb-4">The ritual of Löyly</p>
              <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                Når du heller vann på steinen i en LYKKE sauna utstyrt med Welcome Pack, skal dampen stige mykt og fylle rommet med varmeenergi — ikke brennhet luft eller kjemiske utdampninger. Det er resultatet av at alt — fra volumet til materialene — er tenkt for å skape autentisk löyly. Welcome Pack gir deg verktøyene til å gjøre denne ritualet fullstendig og virkelig.
              </p>
            </div>
          </div>
        </motion.section>

        {/* IMAGE: Design materials and sketches */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-16">
          <div className="rounded-[8px] overflow-hidden">
            <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/df51a8bf9_Zrzutekranu2026-04-11o141539.png" alt="Materialer og skisser på arbeidsbordet" className="w-full h-auto object-cover" />
          </div>
        </motion.div>

        {/* 3. TRE PRINSIPPER */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-20">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16">
              <h2 className="font-heading text-5xl font-light leading-[1.1] mb-8 text-[#2a1810]">
                Form møter<br />landskapet
              </h2>
              <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
                En badstue står aldri helt alene. Den leses sammen med terrenget, lyset og naturen rundt. Arkitekturen handler ikke om ett fastlagt svar, men om hvordan bygget kan tilpasses stedet det skal stå.
              </p>
              <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-10">
                Valg av form, volum og plassering avgjør hvordan badstuen oppleves — både fra innsiden og fra omgivelsene omkring.
              </p>
            </motion.div>

            {/* IMAGE: Architect working on plans */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-16 rounded-[8px] overflow-hidden">
              <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/22e7f61d5_Zrzutekranu2026-04-11o141555.png" alt="Arkitekt jobber med plantegninger" className="w-full h-auto object-cover" />
            </motion.div>

            <div className="space-y-8 mb-16">
              {[
                { title: 'Proporsjoner', desc: 'En badstue trenger riktig forhold mellom høyde, bredde og lengde. Det påvirker både funksjonalitet og estetikk.' },
                { title: 'Plassering på tomten', desc: 'Hvor badstuen ligger bestemmer forhold til huset, utsikt og hvordan solen beveger seg omkring bygget gjennom året.' },
                { title: 'Forhold til terrenget', desc: 'Et bygg som følger terrengets naturlige former fungerer bedre og ser mer naturlig ut enn ett som «flyter over» landskapet.' },
                { title: 'Lys og åpninger', desc: 'Hvor mange vinduer og hvor de plasseres styrer opplevelsen inne i badstuen — både dagslys og utsikt.' },
                { title: 'Materialitet', desc: 'Valget av materiale på fasade og tak påvirker hvordan bygget oppleves og hvordan det dialogiserer med omgivelsene.' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -4 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.05 }}
                  className="bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all"
                >
                  <h3 className="font-heading text-lg font-light text-[#2a1810] mb-3">{item.title}</h3>
                  <p className="font-body text-2xl text-foreground/60">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* IMAGE: Studio workspace with team */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-16 rounded-[8px] overflow-hidden">
              <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/c4fcb75f6_Zrzutekranu2026-04-11o141608.png" alt="Designteam jobber sammen i studio" className="w-full h-auto object-cover" />
            </motion.div>

            <div className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[8px] p-12">
              <h3 className="font-heading text-3xl font-light mb-6">Tre prinsipper</h3>
              <p className="font-body text-2xl text-white/80 leading-relaxed mb-6">
                Vi arbeider etter tre arkitektoniske prinsipper: at badstuen skal tre frem som et tydelig volum, at den skal respektere terrengets topografi, og at materialene skal snakke samme språk som landskapet omkring.
              </p>
              <p className="font-body text-2xl text-white/80 leading-relaxed">
                Det er ikke ett svar som passer alle steder. Hver tomt har sine egenskaper — og hver badstue må finne sin form i forhold til dem.
              </p>
            </div>

            <motion.a whileHover={{ scale: 1.02 }} href="/laer-mer-handverk"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all mt-16">
              Tilbake til Lær Mer <ArrowRight size={16} />
            </motion.a>
          </div>
        </motion.section>

      </div>

      <Footer />
    </div>
  );
}